import { Box } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import {
  NumberFormatValues,
  NumericFormat,
  SourceInfo,
} from "react-number-format";
import InfoTooltip from "../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { SalaryComponent } from "../../../tesSalaryComponents/tesSalaryComponentsAPI";
import { Input } from "./CustomInputs";
import { SalaryType } from "../../../orders/types/salaryType";

type Props = {
  xsCols: [number, number];
  component: SalaryComponent;
  value: string;
  unit: string;
  onChange: (
    onChangeCb: (value: string) => void,
    value: string,
    isUserInput: boolean,
  ) => void;
  disabled?: boolean;
  override?: boolean;
  zone?: string;
  salaryType?: SalaryType;
};

const SalaryComponentRow = ({
  xsCols,
  component,
  value,
  unit,
  onChange,
  disabled,
  override,
  zone,
  salaryType = SalaryType.Hourly,
}: Props) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const MAX_VALUE = salaryType === SalaryType.Hourly ? 999.99 : 99999.99;
  const fieldName = zone
    ? `zonedComponents.${zone}.${component.code}`
    : `components.${component.code}`;

  const isAllowed = (values: NumberFormatValues) => {
    if (!component.allowsZero && values.value === "0") return false;
    if (values.floatValue && values.floatValue > MAX_VALUE) return false;
    return true;
  };

  return (
    <>
      {zone ? (
        // only render the input field (used in zone layouts)
        <Box display="flex" alignItems="center" gap="22px">
          <Controller
            name={fieldName}
            control={control}
            rules={{
              required: {
                value: override ? false : !component.allowsZero,
                message: "",
              },
              validate: (value) => {
                if (!value && component.allowsZero) return true;
                return Number(value) <= MAX_VALUE;
              },
            }}
            render={({ field }) => (
              <NumericFormat
                disabled={disabled}
                isAllowed={(values) => isAllowed(values)}
                name={component.code}
                value={field.value || ""}
                defaultValue={value}
                decimalSeparator=","
                valueIsNumericString
                onValueChange={(values, sourceInfo: SourceInfo) => {
                  onChange(
                    field.onChange,
                    values.value,
                    sourceInfo.source === "event",
                  );
                }}
                maxWidth="135px"
                customInput={Input}
                decimalScale={2}
                unit={unit}
                allowNegative={false}
                error={
                  errors.components &&
                  !!Object.keys(errors["components"]).find(
                    (key) => key === component.code,
                  )
                }
              />
            )}
          />
          {component.infoText && <InfoTooltip title={component.infoText} />}
        </Box>
      ) : (
        // normal layout, render with label and Grid items
        <Box display="flex" alignItems="center" gap={1.5}>
          <Controller
            name={fieldName}
            control={control}
            rules={{
              required: {
                value: override ? false : !component.allowsZero,
                message: "",
              },
              validate: (value) => {
                if (!value && component.allowsZero) return true;
                return Number(value) <= MAX_VALUE;
              },
            }}
            render={({ field }) => (
              <NumericFormat
                disabled={disabled}
                isAllowed={(values) => isAllowed(values)}
                name={component.code}
                value={field.value || ""}
                defaultValue={value}
                decimalSeparator=","
                valueIsNumericString
                onValueChange={(values, sourceInfo: SourceInfo) => {
                  onChange(
                    field.onChange,
                    values.value,
                    sourceInfo.source === "event",
                  );
                }}
                maxWidth="135px"
                customInput={Input}
                decimalScale={2}
                unit={unit}
                allowNegative={false}
                error={
                  errors.components &&
                  !!Object.keys(errors["components"]).find(
                    (key) => key === component.code,
                  )
                }
              />
            )}
          />
          {component.infoText && <InfoTooltip title={component.infoText} />}
        </Box>
      )}
    </>
  );
};

export default SalaryComponentRow;
